import { useState } from "react";
import { X, Trash2 } from "lucide-react";
import { PAYMENT_METHODS, STATUS_OPTIONS } from "./constants";

/**
 * Payment method chooser dialog — surfaces when the operator marks a
 * booking as confirmed/completed and we need to record how the money
 * came in.
 */
export function PaymentMethodDialog({ label, targetStatus, onCancel, onConfirm }) {
  const [selected, setSelected] = useState("cash");
  const targetLabel = STATUS_OPTIONS.find((s) => s.value === targetStatus)?.label || targetStatus;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
      data-testid="payment-method-dialog"
    >
      <div
        className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-1">
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
            Mark as {targetLabel}
          </p>
          <button onClick={onCancel} aria-label="Close" className="text-neutral-400 hover:text-black dark:hover:text-white">
            <X size={16} />
          </button>
        </div>
        <h2 className="font-display text-2xl tracking-tight mb-1">Payment received via?</h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-5 truncate">{label}</p>

        <div className="space-y-2" role="radiogroup">
          {PAYMENT_METHODS.map((m) => (
            <label
              key={m.value}
              className={`flex items-center justify-between gap-3 px-4 py-3 rounded border cursor-pointer transition-colors ${
                selected === m.value
                  ? "border-black dark:border-white bg-black/[0.03] dark:bg-white/[0.05]"
                  : "border-black/10 dark:border-white/10 hover:border-black/30 dark:hover:border-white/30"
              }`}
              data-testid={`method-${m.value}`}
            >
              <span className="text-sm">{m.label}</span>
              <input
                type="radio"
                name="payment_method"
                value={m.value}
                checked={selected === m.value}
                onChange={() => setSelected(m.value)}
                className="accent-black dark:accent-white"
              />
            </label>
          ))}
        </div>

        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-4 leading-relaxed">
          For Cash / Bank Transfer / Card on Office / PayPal, the booking will automatically be marked as <strong>paid</strong>. Selecting Stripe just records the method without changing payment status.
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="border border-black/15 dark:border-white/15 px-5 py-2 text-sm hover:border-black dark:hover:border-white transition-colors rounded"
            data-testid="method-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selected)}
            className="bg-black text-white dark:bg-white dark:text-black px-5 py-2 text-sm hover:opacity-90 transition-opacity rounded"
            data-testid="method-confirm"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Destructive delete confirmation. Awaits the parent's async
 * `onConfirmed` so the primary button can show a "Deleting…" state
 * while the DELETE flight is in progress.
 */
export function DeleteBookingDialog({ booking, onCancel, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onConfirmed(); } finally { setBusy(false); }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
      data-testid="delete-booking-dialog"
    >
      <div
        className="bg-white dark:bg-[#1F2937] max-w-md w-full p-6 rounded border border-black/10 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl mb-2 flex items-center gap-2 text-rose-600 dark:text-rose-400">
          <Trash2 size={18} /> Delete booking
        </h3>
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-1">
          Are you sure you want to permanently delete this booking?
        </p>
        <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded p-3 my-3 text-xs space-y-1">
          <div><span className="text-neutral-500 dark:text-neutral-400">ID: </span><code>{booking.id.slice(0, 8).toUpperCase()}</code></div>
          <div><span className="text-neutral-500 dark:text-neutral-400">Customer: </span>{booking.full_name} · {booking.user_email}</div>
          <div><span className="text-neutral-500 dark:text-neutral-400">Vehicle: </span>{booking.vehicle_snapshot?.brand} {booking.vehicle_snapshot?.name}</div>
          <div><span className="text-neutral-500 dark:text-neutral-400">Total: </span>€{Number(booking.total || 0).toFixed(2)}</div>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 italic">
          This action is permanent and cannot be undone. An audit log entry will be recorded.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="border border-black/15 dark:border-white/15 px-4 py-1.5 text-sm rounded hover:border-black dark:hover:border-white"
            data-testid="delete-booking-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="bg-rose-600 text-white px-4 py-1.5 text-sm rounded inline-flex items-center gap-1 hover:opacity-90 disabled:opacity-50"
            data-testid="delete-booking-confirm"
          >
            <Trash2 size={12} /> {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
